import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { motion, AnimatePresence } from 'framer-motion'
import clsx from 'clsx'
import type { AppDispatch, RootState } from '../store'
import { loadAyah } from '../store/slices/ayahSlice'
import { clearEvaluation } from '../store/slices/evaluationSlice'
import { reset as resetRecitation } from '../store/slices/recitationSlice'
import AyahSelector from '../components/AyahSelector'
import AyahDisplay from '../components/AyahDisplay'
import RecordButton from '../components/RecordButton'
import WaveformPlayer from '../components/WaveformPlayer'
import FeedbackPanel from '../components/FeedbackPanel'
import ProgressDashboard from '../components/ProgressDashboard'
import UsageBanner from '../components/UsageBanner'
import { isDev } from '../data/env'
import Onboarding from '../components/Onboarding'
import ConfettiCanvas from '../components/Confetti'
import ShareCard from '../components/ShareCard'
import KeyboardShortcutsModal, { useKeyboardShortcuts } from '../components/KeyboardShortcuts'
import { useToast } from '../components/Toast'

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'ur', label: 'اردو', flag: '🇵🇰' },
  { code: 'ms', label: 'Melayu', flag: '🇲🇾' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'id', label: 'Indonesia', flag: '🇮🇩' },
  { code: 'bn', label: 'বাংলা', flag: '🇧🇩' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
];

export default function PracticePage() {
  const dispatch = useDispatch<AppDispatch>()
  const evaluation = useSelector((state: RootState) => state.evaluation.current)
  const currentAyah = useSelector((state: RootState) => state.ayah.current)
  const selectedSurah = useSelector((state: RootState) => state.ayah.selectedSurah)
  const selectedAyah = useSelector((state: RootState) => state.ayah.selectedAyah)
  const totalAyahs = useSelector((state: RootState) => state.ayah.totalAyahs)
  const ayahLoading = useSelector((state: RootState) => state.ayah.loading)
  const toast = useToast()

  // Dev: Gemini model selector
  const [geminiModel, setGeminiModel] = useState(() =>
    localStorage.getItem('tajweed_gemini_model') || 'gemini-3.1-pro-preview'
  )
  const handleModelChange = (model: string) => {
    setGeminiModel(model)
    localStorage.setItem('tajweed_gemini_model', model)
  }

  // Language preference (shared with live session)
  const [feedbackLang, setFeedbackLang] = useState(() =>
    localStorage.getItem('tajweed_session_lang') || 'en'
  )
  const [showLangPicker, setShowLangPicker] = useState(false)
  const handleLangChange = (code: string) => {
    setFeedbackLang(code)
    localStorage.setItem('tajweed_session_lang', code)
    setShowLangPicker(false)
  }
  const currentLang = LANGUAGES.find(l => l.code === feedbackLang) || LANGUAGES[0]

  // Difficulty preference — persists, applies to BOTH main eval and per-finding practice
  const [difficulty, setDifficulty] = useState<'easy' | 'normal' | 'strict'>(() => {
    const stored = localStorage.getItem('tajweed_practice_difficulty')
    return stored === 'normal' || stored === 'strict' ? stored : 'easy'
  })
  const handleDifficultyChange = (d: 'easy' | 'normal' | 'strict') => {
    setDifficulty(d)
    localStorage.setItem('tajweed_practice_difficulty', d)
    // Notify any open FindingCards (which read difficulty from localStorage too)
    window.dispatchEvent(new CustomEvent('tajweed:difficulty-changed'))
  }

  // Onboarding
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return !localStorage.getItem('tajweed_onboarded')
  })

  // Confetti on high score
  const [showConfetti, setShowConfetti] = useState(false)
  const [prevEvalId, setPrevEvalId] = useState<string | null>(null)

  useEffect(() => {
    if (evaluation && evaluation.meta.recitationId !== prevEvalId) {
      setPrevEvalId(evaluation.meta.recitationId)
      const bogus = ['silence', 'humming', 'wrong_language', 'wrong_ayah', 'unintelligible'].includes(
        (evaluation as { status?: string }).status || ''
      )
      if (bogus) {
        // Don't celebrate or congratulate when we couldn't evaluate the audio
      } else if (evaluation.overall.score0100 >= 90) {
        setShowConfetti(true)
        toast.success('Excellent!', `You scored ${Math.round(evaluation.overall.score0100)}/100!`)
      } else if (evaluation.overall.score0100 >= 70) {
        toast.info('Good job!', `Score: ${Math.round(evaluation.overall.score0100)}/100`)
      }
    }
  }, [evaluation, prevEvalId, toast])

  // Share card
  const [showShare, setShowShare] = useState(false)

  // Bookmarks
  const [bookmarks, setBookmarks] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('tajweed_bookmarks') || '[]')
    } catch {
      return []
    }
  })

  const bookmarkKey = currentAyah ? `${currentAyah.surah}:${currentAyah.ayah}` : ''
  const isBookmarked = bookmarks.includes(bookmarkKey)

  const toggleBookmark = useCallback(() => {
    if (!currentAyah) return
    const key = `${currentAyah.surah}:${currentAyah.ayah}`
    setBookmarks((prev) => {
      const next = prev.includes(key) ? prev.filter((b) => b !== key) : [...prev, key]
      localStorage.setItem('tajweed_bookmarks', JSON.stringify(next))
      if (next.includes(key)) {
        toast.success('Bookmarked', `${currentAyah.surahName} ${currentAyah.surah}:${currentAyah.ayah}`)
      }
      return next
    })
  }, [currentAyah, toast])

  // Keyboard shortcuts
  const { showHelp, setShowHelp } = useKeyboardShortcuts({
    onRecord: () => {
      // Space triggers record button via DOM
      const btn = document.querySelector('[data-record-btn]') as HTMLButtonElement
      btn?.click()
    },
    onSubmit: () => {
      const btn = document.querySelector('[data-submit-btn]') as HTMLButtonElement
      btn?.click()
    },
    onReRecord: () => {
      const btn = document.querySelector('[data-rerecord-btn]') as HTMLButtonElement
      btn?.click()
    },
    onNextAyah: () => {
      if (selectedAyah < totalAyahs) {
        dispatch(clearEvaluation())
        dispatch(resetRecitation())
        dispatch(loadAyah({ surah: selectedSurah, ayah: selectedAyah + 1 }))
      }
    },
    onPrevAyah: () => {
      if (selectedAyah > 1) {
        dispatch(clearEvaluation())
        dispatch(resetRecitation())
        dispatch(loadAyah({ surah: selectedSurah, ayah: selectedAyah - 1 }))
      }
    },
  })

  // Auto-load Al-Fatiha on first visit
  useEffect(() => {
    if (!currentAyah && !ayahLoading) {
      dispatch(loadAyah({ surah: 1, ayah: 1 }))
    }
  }, [dispatch, currentAyah, ayahLoading])

  const handleNextAyah = () => {
    if (selectedAyah < totalAyahs) {
      dispatch(clearEvaluation())
      dispatch(resetRecitation())
      dispatch(loadAyah({ surah: selectedSurah, ayah: selectedAyah + 1 }))
    }
  }

  const handleRetry = () => {
    dispatch(clearEvaluation())
    dispatch(resetRecitation())
  }

  return (
    <div className="max-w-4xl mx-auto w-full px-4 py-6 space-y-6">
      {/* Onboarding modal */}
      {showOnboarding && (
        <Onboarding onComplete={() => setShowOnboarding(false)} />
      )}

      {/* Confetti */}
      <ConfettiCanvas trigger={showConfetti} onComplete={() => setShowConfetti(false)} />

      {/* Keyboard shortcuts modal */}
      <KeyboardShortcutsModal open={showHelp} onClose={() => setShowHelp(false)} />

      {/* Share card modal */}
      {evaluation && currentAyah && (
        <ShareCard
          open={showShare}
          onClose={() => setShowShare(false)}
          score={evaluation.overall.score0100}
          surah={currentAyah.surah}
          ayah={currentAyah.ayah}
          surahName={currentAyah.surahName}
          streak={0}
          findings={evaluation.findings.length}
        />
      )}

      <UsageBanner />

      {/* Language picker */}
      {/* Close picker when clicking outside.
          z-20 sits BELOW the controls row (z-30) so the dropdown buttons
          stay clickable; clicks anywhere else fall through to this overlay
          and close the picker. */}
      {showLangPicker && (
        <div className="fixed inset-0 z-20" onClick={() => setShowLangPicker(false)} />
      )}
      <div className="flex justify-end items-center gap-2 relative z-30">
        {/* Dev-only: model selector. Hidden when VITE_ENV=production. */}
        {isDev && (
          <select
            value={geminiModel}
            onChange={(e) => handleModelChange(e.target.value)}
            className="text-[11px] px-2 py-1 rounded-md border border-dashed border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 cursor-pointer"
            title="[DEV] Gemini model"
          >
            <option value="gemini-3.1-pro-preview">3.1 Pro</option>
            <option value="gemini-2.5-flash">2.5 Flash</option>
            <option value="gemini-2.5-flash-lite">2.5 Flash Lite</option>
          </select>
        )}

        {/* Difficulty selector — applies to grading harshness across the app */}
        <div
          className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-0.5 text-[11px] font-medium"
          title="How strict the grading should be"
        >
          {(['easy', 'normal', 'strict'] as const).map((d) => (
            <button
              key={d}
              onClick={() => handleDifficultyChange(d)}
              className={clsx(
                'px-2.5 py-1 rounded-full transition-all capitalize',
                difficulty === d
                  ? d === 'easy'
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : d === 'normal'
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'bg-rose-500 text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              )}
              title={
                d === 'easy'
                  ? 'Easy — only catch wrong-letter mistakes'
                  : d === 'normal'
                  ? 'Normal — catch what a regular teacher would'
                  : 'Strict — ijazah-level precision'
              }
            >
              {d}
            </button>
          ))}
        </div>

        <button
          onClick={() => setShowLangPicker(!showLangPicker)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          title="Feedback language"
        >
          <span>{currentLang.flag}</span>
          <span className="text-xs">{currentLang.label}</span>
          <svg className="w-3.5 h-3.5 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </button>
        <AnimatePresence>
          {showLangPicker && (
            <motion.div
              initial={{ opacity: 0, y: -5, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -5, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute top-full right-0 mt-1 z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-2 min-w-[180px]"
            >
              <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 px-2 py-1 font-medium">
                Feedback Language
              </p>
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => handleLangChange(l.code)}
                  className={clsx(
                    'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors',
                    feedbackLang === l.code
                      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  )}
                >
                  <span>{l.flag}</span>
                  <span>{l.label}</span>
                  {feedbackLang === l.code && (
                    <svg className="w-4 h-4 ml-auto text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bookmarks bar */}
      {bookmarks.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 flex-wrap"
        >
          <span className="text-xs text-gray-500 dark:text-gray-400">Bookmarks:</span>
          {bookmarks.map((bk) => {
            const [s, a] = bk.split(':').map(Number)
            return (
              <button
                key={bk}
                onClick={() => {
                  dispatch(clearEvaluation())
                  dispatch(resetRecitation())
                  dispatch(loadAyah({ surah: s, ayah: a }))
                }}
                className="badge border border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-xs cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors"
              >
                {bk}
              </button>
            )
          })}
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <AyahSelector />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
      >
        <div className="relative">
          <AyahDisplay />
          {/* Bookmark button */}
          {currentAyah && (
            <button
              onClick={toggleBookmark}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-amber-500 transition-colors"
              title={isBookmarked ? 'Remove bookmark' : 'Bookmark this ayah'}
            >
              {isBookmarked ? (
                <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5 2h14a1 1 0 011 1v19.143a.5.5 0 01-.766.424L12 18.03l-7.234 4.536A.5.5 0 014 22.143V3a1 1 0 011-1z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                </svg>
              )}
            </button>
          )}
        </div>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <div className="card flex items-center justify-center min-h-[200px]">
          <RecordButton />
        </div>
        <WaveformPlayer />
      </motion.div>

      <AnimatePresence mode="wait">
        {evaluation && (
          <motion.div
            key="feedback"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
          >
            <FeedbackPanel language={feedbackLang} />

            {/* Suggested next actions */}
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <button onClick={handleRetry} className="btn-secondary text-sm py-2 px-6">
                Retry This Ayah
              </button>
              {selectedAyah < totalAyahs && (
                <button onClick={handleNextAyah} className="btn-primary text-sm py-2 px-6">
                  Next Ayah
                </button>
              )}
              <button
                onClick={() => setShowShare(true)}
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
                </svg>
                Share
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ProgressDashboard />
    </div>
  )
}
